import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { usStatesData } from '../../mocks/usStates';
import StateCard from './components/StateCard';
import RegionFilter from './components/RegionFilter';

const StatesPage = () => {
  const navigate = useNavigate();
  const [activeRegion, setActiveRegion] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'population' | 'tax'>('name');

  useDocumentMeta({
    title: 'US States Tax Calculator Directory - All 50 States Income Tax Rates 2026',
    description:
      'Browse salary tax calculators for all 50 US states plus DC and Puerto Rico. Compare state income tax rates, find popular cities, and calculate your take-home pay by location.',
    keywords:
      'US states tax calculator, state income tax rates, salary calculator by state, take-home pay by state, no income tax states, state tax comparison 2026',
    canonical: 'https://mysalarycalculator.net/states',
    ogTitle: 'US States Tax Calculator Directory - All 50 States Income Tax Rates 2026',
    ogDescription:
      'Compare income tax rates across all 50 US states. Find calculators for popular cities and maximize your take-home pay.',
    ogUrl: 'https://mysalarycalculator.net/states',
    twitterCard: 'summary_large_image',
    twitterTitle: 'US States Tax Calculator Directory - All 50 States',
    twitterDescription:
      'Compare income tax rates across all 50 US states. Find calculators for popular cities and maximize your take-home pay.',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://mysalarycalculator.net/' },
          { '@type': 'ListItem', position: 2, name: 'US States', item: 'https://mysalarycalculator.net/states' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'US States Tax Calculator Directory',
        description:
          'Browse salary tax calculators for all 50 US states plus DC and Puerto Rico.',
        url: 'https://mysalarycalculator.net/states',
        isPartOf: { '@type': 'WebSite', name: 'Salary Tax Calculator', url: 'https://mysalarycalculator.net/' },
      },
    ],
  });

  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    usStatesData.forEach((s) => {
      counts[s.region] = (counts[s.region] || 0) + 1;
    });
    return counts;
  }, []);

  const filteredStates = useMemo(() => {
    let list = usStatesData;

    if (activeRegion !== 'All') {
      list = list.filter((s) => s.region === activeRegion);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.abbreviation.toLowerCase().includes(q) ||
          s.capital.toLowerCase().includes(q) ||
          s.cities.some((c) => c.name.toLowerCase().includes(q)),
      );
    }

    if (sortBy === 'population') {
      list = [...list].sort((a, b) => {
        const parseP = (p: string) => {
          const n = parseFloat(p.replace(/[KM]/g, ''));
          return p.includes('M') ? n * 1000 : n;
        };
        return parseP(b.population) - parseP(a.population);
      });
    } else if (sortBy === 'tax') {
      list = [...list].sort((a, b) => parseFloat(a.topRate) - parseFloat(b.topRate));
    } else {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    }

    return list;
  }, [activeRegion, searchQuery, sortBy]);

  const noTaxStates = usStatesData.filter((s) => s.topRate === '0%');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" role="banner">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <a href="/" aria-label="Salary Tax Calculator Home">
                <img
                  src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
                  alt="Salary Tax Calculator Logo"
                  className="h-10 w-auto cursor-pointer"
                  width="40"
                  height="40"
                />
              </a>
              <div className="hidden md:block">
                <span className="text-xl font-bold text-gray-900">
                  <a href="/" className="hover:text-teal-600 transition-colors cursor-pointer">
                    Salary Tax Calculator
                  </a>
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <a href="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">
                Calculator
              </a>
              <a href="/blog" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">
                Blog
              </a>
              <a href="/states" className="text-sm font-medium text-teal-600 cursor-pointer hidden md:block">
                US States
              </a>
              <a href="/sitemap" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">
                Sitemap
              </a>
            </div>
          </div>
        </nav>
      </header>

      <main role="main">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-2">
          <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-home-4-line text-xs"></i>
              Home
            </a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">US States</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <img
              src="https://public.readdy.ai/ai/img_res/06bc21d25ebdd34ae2c609f4562a0014.jpg"
              alt="US States directory background"
              className="w-full h-full object-cover opacity-15"
              width="1600"
              height="600"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-gray-900/30 to-gray-900/70" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/20 border border-teal-500/30 rounded-full text-teal-300 text-sm font-medium mb-6">
              <i className="ri-map-pin-line"></i> 52 Locations
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              <strong>US States Salary Tax Calculator Directory 2026</strong>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-6">
              Compare <strong>state income tax rates</strong> across all 50 states, DC, and Puerto Rico. Find calculators for popular cities and <strong>maximize your take-home pay</strong>.
            </p>

            {/* Quick stats */}
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="text-center">
                <span className="text-2xl font-bold text-white">52</span>
                <p className="text-xs text-gray-400 mt-0.5">States &amp; Territories</p>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-center">
                <span className="text-2xl font-bold text-emerald-400">{noTaxStates.length}</span>
                <p className="text-xs text-gray-400 mt-0.5">No Income Tax</p>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-center">
                <span className="text-2xl font-bold text-white">200+</span>
                <p className="text-xs text-gray-400 mt-0.5">Cities Covered</p>
              </div>
            </div>

            {/* Search */}
            <div className="max-w-lg mx-auto relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search by state, city, or abbreviation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-teal-400 focus:bg-white/15 transition-all"
              />
            </div>
          </div>
        </section>

        {/* No-Tax States Highlight */}
        <section className="py-10 bg-emerald-50 border-b border-emerald-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-lg">
                <i className="ri-shield-check-line text-white text-sm"></i>
              </div>
              <div>
                <h4 className="font-bold text-emerald-900 text-base">
                  <a href="#no-tax" className="hover:underline cursor-pointer">Zero Income Tax States</a>
                </h4>
                <p className="text-xs text-emerald-700">Keep 100% of your state tax — these states have no income tax</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {noTaxStates.map((s) => (
                <a
                  key={s.slug}
                  href={`/states/${s.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const hasCalc = s.cities.some((c) => c.hasCalculator);
                    navigate(hasCalc ? `/usa/${s.slug}` : `/states/${s.slug}`);
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white border border-emerald-200 hover:border-emerald-400 rounded-lg text-sm font-medium text-emerald-800 hover:text-emerald-900 hover:shadow-sm transition-all cursor-pointer"
                >
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">
                    {s.abbreviation}
                  </span>
                  {s.name}
                  <i className="ri-arrow-right-s-line text-emerald-400 text-sm"></i>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Filters + Grid */}
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Filter bar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <RegionFilter
                activeRegion={activeRegion}
                onRegionChange={setActiveRegion}
                counts={regionCounts}
              />
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Sort:</span>
                <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
                  {([
                    { key: 'name', label: 'A-Z', icon: 'ri-sort-alphabet-asc' },
                    { key: 'population', label: 'Pop', icon: 'ri-group-line' },
                    { key: 'tax', label: 'Tax', icon: 'ri-percent-line' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className={`${opt.icon} mr-1`}></i>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {activeRegion === 'All' && !searchQuery
                  ? <><strong>All 50 States</strong> &amp; Territories Tax Calculators</>
                  : `${filteredStates.length} Result${filteredStates.length !== 1 ? 's' : ''}`}
              </h2>
            </div>

            {/* Grid */}
            {filteredStates.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-4">
                  <i className="ri-map-pin-line text-3xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No states found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your search or region filter.</p>
                <button
                  onClick={() => {
                    setActiveRegion('All');
                    setSearchQuery('');
                  }}
                  className="mt-4 px-5 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStates.map((state) => (
                  <StateCard key={state.slug} state={state} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Tax Comparison Table */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
              <strong>State Income Tax Rate Comparison 2026</strong>
            </h2>
            <p className="text-gray-500 text-sm text-center mb-8">
              Quick reference of top marginal income tax rates across all US states for salary planning
            </p>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">State</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Top Rate</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Region</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Population</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-700"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...usStatesData]
                      .sort((a, b) => parseFloat(a.topRate) - parseFloat(b.topRate))
                      .slice(0, 15)
                      .map((s, i) => {
                        const hasCalc = s.cities.some((c) => c.hasCalculator);
                        return (
                          <tr
                            key={s.slug}
                            className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                              i % 2 === 0 ? '' : 'bg-gray-50/50'
                            }`}
                          >
                            <td className="px-4 py-3">
                              <a
                                href={hasCalc ? `/usa/${s.slug}` : `/states/${s.slug}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(hasCalc ? `/usa/${s.slug}` : `/states/${s.slug}`);
                                }}
                                className="font-medium text-gray-900 hover:text-teal-600 transition-colors cursor-pointer"
                              >
                                {s.name}
                              </a>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{s.stateTaxType}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                  s.topRate === '0%'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : parseFloat(s.topRate) <= 5
                                    ? 'bg-teal-100 text-teal-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {s.topRate}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500">{s.region}</td>
                            <td className="px-4 py-3 text-gray-500">{s.population}</td>
                            <td className="px-4 py-3 text-right">
                              <a
                                href={hasCalc ? `/usa/${s.slug}` : `/states/${s.slug}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(hasCalc ? `/usa/${s.slug}` : `/states/${s.slug}`);
                                }}
                                className="text-teal-600 hover:text-teal-700 cursor-pointer"
                              >
                                <i className="ri-arrow-right-line text-sm"></i>
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-gray-400 text-center mt-3">
              Showing 15 lowest-tax states. Browse all states above for the complete list.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2"><strong>Calculate Your Take-Home Pay by State</strong></h2>
            <p className="text-gray-500 text-sm mb-6 max-w-xl mx-auto">
              Choose a state above or jump straight to our most popular city salary calculators
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { name: 'Manhattan, NY', path: '/usa/ny/manhattan' },
                { name: 'San Francisco, CA', path: '/usa/ca/san-francisco' },
                { name: 'Austin, TX', path: '/usa/tx/austin' },
                { name: 'Miami, FL', path: '/usa/fl/miami' },
                { name: 'Chicago, IL', path: '/usa/il/chicago' },
                { name: 'Seattle, WA', path: '/usa/wa/seattle' },
              ].map((loc) => (
                <a
                  key={loc.path}
                  href={loc.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(loc.path);
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-teal-50 border border-gray-200 hover:border-teal-300 rounded-xl text-sm font-medium text-gray-700 hover:text-teal-700 transition-all cursor-pointer"
                >
                  <i className="ri-calculator-line text-gray-400"></i>
                  {loc.name}
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="grid grid-cols-1 md:grid-cols-4 gap-8" aria-label="Footer navigation">
            <div>
              <h3 className="text-lg font-bold mb-4">Salary Calculator</h3>
              <p className="text-gray-400 text-sm">
                Accurate tax calculations for locations worldwide. Plan your finances with confidence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">
                <a href="/states" className="hover:text-teal-400 transition-colors cursor-pointer">Popular States</a>
              </h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/states/ca" className="hover:text-white transition-colors cursor-pointer">California</a></li>
                <li><a href="/states/tx" className="hover:text-white transition-colors cursor-pointer">Texas</a></li>
                <li><a href="/states/ny" className="hover:text-white transition-colors cursor-pointer">New York</a></li>
                <li><a href="/states/fl" className="hover:text-white transition-colors cursor-pointer">Florida</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/blog" className="hover:text-white transition-colors cursor-pointer">Tax Blog</a></li>
                <li><a href="/sitemap" className="hover:text-white transition-colors cursor-pointer">Sitemap</a></li>
                <li><a href="/" className="hover:text-white transition-colors cursor-pointer">Calculator</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
              </ul>
            </div>
          </nav>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>
              &copy; 2026 Salary Tax Calculator. All rights reserved. |{' '}
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Web Design
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StatesPage;
